/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo, useCallback } from 'react';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Text, Stack } from '@atlaskit/primitives/compiled';
import CodeBlock from '@atlaskit/code/code-block';
import Button from '@atlaskit/button/default/button';
import Spinner from '@atlaskit/spinner/spinner';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { useSetAtom, useAtomValue } from 'jotai';

import { type MessageRole } from '../../contexts/AsideContext';
import {
	ProposedCypherQueryAtom,
	CypherQueryAtom,
	TriggerCypherFormatAtom,
	CyphieModeAtom,
	type CyphieMode,
} from '../../state/LabsAtoms';

export type ChatMessageProps = {
	messageRole: MessageRole;
	content: string;
	isLoading?: boolean;
	rawCypherQuery?: string;
	assistantName?: string;
	cyphieMode?: CyphieMode;
};

const styles = cssMap({
	messageContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	userMessage: {
		alignSelf: 'flex-end',
		backgroundColor: token('color.background.selected'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		maxWidth: '85%',
		whiteSpace: 'pre-wrap',
	},
	assistantMessage: {
		alignSelf: 'flex-start',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		maxWidth: '85%',
		whiteSpace: 'pre-wrap',
	},
	loadingMessage: {
		alignSelf: 'flex-start',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	codeBlockWrapper: {
		marginBlockStart: token('space.100'),
		marginBlockEnd: token('space.100'),
	},
	actionRow: {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		justifyContent: 'flex-start',
		marginBlockStart: token('space.100'),
		gap: token('space.100'),
	},
	actionButton: {
		flex: '0 1 auto',
		minWidth: '140px',
	},
});

type ContentPart =
	| { type: 'text'; content: string }
	| { type: 'code'; language: string; content: string };

/**
 * Parses content string and splits it into text and code block parts
 */
function parseContent(content: string): ContentPart[] {
	const parts: ContentPart[] = [];
	const codeBlockRegex = /```(\w+)?\n?([\s\S]*?)```/g;
	let lastIndex = 0;
	let match;

	while ((match = codeBlockRegex.exec(content)) !== null) {
		// Add text before the code block
		if (match.index > lastIndex) {
			parts.push({
				type: 'text',
				content: content.slice(lastIndex, match.index),
			});
		}

		// Add the code block
		parts.push({
			type: 'code',
			language: match[1] || 'text',
			content: match[2].trim(),
		});

		lastIndex = match.index + match[0].length;
	}

	// Add remaining text after the last code block
	if (lastIndex < content.length) {
		parts.push({
			type: 'text',
			content: content.slice(lastIndex),
		});
	}

	return parts.length > 0 ? parts : [{ type: 'text', content }];
}

/**
 * Renders text with basic markdown-style bold (**text**)
 */
function renderTextWithBold(text: string) {
	const parts = text.split(/(\*\*[^*]+\*\*)/g);
	return parts.map((part, index) => {
		if (part.startsWith('**') && part.endsWith('**')) {
			return (
				<Text key={index} as="strong">
					{part.slice(2, -2)}
				</Text>
			);
		}
		return part;
	});
}

const CypherCodeBlock = ({ content }: { language: string; content: string }) => {
	const codeLanguage = 'plaintext' as const;

	return (
		<Box xcss={styles.codeBlockWrapper}>
			<CodeBlock text={content} language={codeLanguage} showLineNumbers={false} />
		</Box>
	);
};

export const ChatMessage = ({
	messageRole,
	content,
	isLoading = false,
	rawCypherQuery,
	assistantName = 'Assistant',
	cyphieMode,
}: ChatMessageProps): JSX.Element => {
	const isUser = messageRole === 'user';
	const setProposedQuery = useSetAtom(ProposedCypherQueryAtom);
	const currentQuery = useAtomValue(CypherQueryAtom);
	const setCypherQuery = useSetAtom(CypherQueryAtom);
	const setTriggerFormat = useSetAtom(TriggerCypherFormatAtom);
	const setCyphieMode = useSetAtom(CyphieModeAtom);

	const parsedContent = useMemo(() => parseContent(content), [content]);
	const hasExistingQuery = Boolean(currentQuery?.trim());

	const queriesAreDifferent = useMemo(() => {
		if (!currentQuery || !rawCypherQuery) {
			return true;
		}
		const normalizeQuery = (query: string) => {
			let normalized = query.replace(/\/\/.*$/gm, '');
			normalized = normalized.replace(/\/\*[\s\S]*?\*\//g, '');
			normalized = normalized.replace(/\s+/g, ' ');
			normalized = normalized.replace(/\}\s*\)\s*-\s*\[:/g, '})-[:');
			normalized = normalized.replace(/\)\s*-\s*\[:/g, ')-[:');
			normalized = normalized.replace(/\}\s*-\s*\[:/g, '}-[:');
			normalized = normalized.replace(/\s*->\s*/g, '->');
			return normalized.trim();
		};
		const normalizedCurrent = normalizeQuery(currentQuery);
		const normalizedProposed = normalizeQuery(rawCypherQuery);

		return normalizedCurrent !== normalizedProposed;
	}, [currentQuery, rawCypherQuery]);

	const handleUseInEditor = useCallback(() => {
		if (rawCypherQuery) {
			setCypherQuery(rawCypherQuery);
			setCyphieMode(cyphieMode ?? null);
			setTimeout(() => {
				setTriggerFormat(true);
			}, 50);
		}
	}, [rawCypherQuery, setCypherQuery, setTriggerFormat, setCyphieMode, cyphieMode]);

	const handleReviewInEditor = useCallback(() => {
		if (rawCypherQuery) {
			setProposedQuery({
				query: rawCypherQuery,
			});
			setCyphieMode(cyphieMode ?? null);
		}
	}, [rawCypherQuery, setProposedQuery, setCyphieMode, cyphieMode]);

	if (isLoading) {
		return (
			<Box xcss={styles.messageContainer}>
				<Text size="small" color="color.text.subtlest">
					{assistantName}
				</Text>
				<Box xcss={styles.loadingMessage}>
					<Spinner size="small" />
					<Text color="color.text.subtlest">{content}</Text>
				</Box>
			</Box>
		);
	}

	return (
		<Box xcss={styles.messageContainer}>
			<Text size="small" color="color.text.subtlest">
				{isUser ? 'You' : assistantName}
			</Text>
			<Box xcss={isUser ? styles.userMessage : styles.assistantMessage}>
				<Stack space="space.050">
					{parsedContent.map((part, index) => {
						if (part.type === 'code') {
							return (
								<CypherCodeBlock key={index} language={part.language} content={part.content} />
							);
						}
						return <Text key={index}>{renderTextWithBold(part.content)}</Text>;
					})}
					{rawCypherQuery && (!hasExistingQuery || queriesAreDifferent) && (
						<Box xcss={styles.actionRow}>
							<Box xcss={styles.actionButton}>
								<Button
									appearance="primary"
									onClick={handleUseInEditor}
									iconBefore={(iconProps) => <VideoPlayOverlayIcon {...iconProps} />}
								>
									Use in Editor
								</Button>
							</Box>
							{hasExistingQuery && queriesAreDifferent && (
								<Box xcss={styles.actionButton}>
									<Button
										appearance="default"
										onClick={handleReviewInEditor}
										iconBefore={(iconProps) => <CheckCircleIcon {...iconProps} />}
									>
										Review Changes
									</Button>
								</Box>
							)}
						</Box>
					)}
				</Stack>
			</Box>
		</Box>
	);
};
